import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Newspaper } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { PageHero, usePageTitle } from "../components/Shared";
import { IMAGES } from "../data/content";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const COPY = {
  fr: {
    meta: "Blog & guides",
    eyebrow: "Blog & guides",
    h1a: "Comprendre les démarches",
    h1b: "au Togo, simplement.",
    sub: "Guides pratiques, actualités administratives et conseils pour la diaspora, les entrepreneurs et les étrangers au Togo.",
    loading: "Chargement des articles…",
    emptyTitle: "Les articles arrivent bientôt",
    emptyText: "Notre équipe prépare des guides pratiques sur les démarches au Togo. Revenez vite !",
    read: "Lire l'article",
    imgAlt: "Lomé, Togo",
  },
  en: {
    meta: "Blog & guides",
    eyebrow: "Blog & guides",
    h1a: "Understanding procedures",
    h1b: "in Togo, made simple.",
    sub: "Practical guides, administrative news and advice for the diaspora, entrepreneurs and foreign nationals in Togo.",
    loading: "Loading articles…",
    emptyTitle: "Articles are coming soon",
    emptyText: "Our team is preparing practical guides about procedures in Togo. Come back soon!",
    read: "Read the article",
    imgAlt: "Lomé, Togo",
  },
};

const formatDate = (iso, lang) => {
  try {
    return new Date(iso).toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

export default function Blog() {
  const { lang } = useLang();
  const c = COPY[lang];
  usePageTitle(c.meta);
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    axios.get(`${API}/blog`).then((r) => setArticles(r.data)).catch(() => setArticles([]));
  }, []);

  return (
    <div data-testid="blog-page">
      <PageHero
        eyebrow={c.eyebrow}
        lines={[<>{c.h1a}</>, <>{c.h1b}</>]}
        sub={c.sub}
        image={IMAGES.lome}
        imageAlt={c.imgAlt}
      />
      <section className="bg-ivory py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-6">
          {articles === null ? (
            <div className="py-16 text-center text-sm text-slate-400">{c.loading}</div>
          ) : articles.length === 0 ? (
            <Reveal>
              <div className="rounded-3xl border border-dashed border-brand/20 bg-white p-14 text-center">
                <Newspaper className="mx-auto h-10 w-10 text-brand/40" />
                <h2 className="mt-4 font-display text-xl font-bold text-slate-900">{c.emptyTitle}</h2>
                <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">{c.emptyText}</p>
              </div>
            </Reveal>
          ) : (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {articles.map((a, i) => (
                <Reveal key={a.id} delay={i * 0.07}>
                  <Link
                    to={`/blog/${a.slug}`}
                    data-testid={`blog-card-${a.slug}`}
                    className="group flex h-full flex-col overflow-hidden rounded-3xl border border-brand/10 bg-white shadow-sm transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-gold/40 hover:shadow-xl"
                  >
                    {a.cover_url ? (
                      <img src={`${BACKEND}${a.cover_url}`} alt={a.titre} loading="lazy" className="h-52 w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    ) : (
                      <div className="grid h-52 w-full place-items-center bg-brand-deep">
                        <span className="font-display text-3xl font-extrabold text-gold">DOC TOGO</span>
                      </div>
                    )}
                    <div className="flex flex-1 flex-col p-7">
                      <div className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-gold">
                        {formatDate(a.created_at, lang)} · {a.auteur_nom}
                      </div>
                      <h2 className="mt-3 font-display text-xl font-bold leading-snug text-slate-900 transition-colors group-hover:text-brand">
                        {a.titre}
                      </h2>
                      <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate-600">{a.extrait}</p>
                      <span className="mt-5 inline-flex items-center gap-2 font-display text-sm font-bold text-brand">
                        {c.read}
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
